/**
 * Abrir a conversa marca as mensagens como lidas (§8.2).
 *
 * ── Por que isto não existia, e o que custava ─────────────────────────────
 *
 * O contador de não lidas vem do motor de conversa, e ninguém nunca o avisava
 * de nada. Abrir uma conversa com seis mensagens novas, ler todas e sair
 * deixava o seis lá.
 *
 * Contador que não zera é pior que contador nenhum: quem confia nele uma vez e
 * é traído para de olhar para ele, e aí o número deixa de dirigir o trabalho.
 * Relatado em 02/09/2026.
 *
 * ── Uma marcação por vez, e de novo quando o número sobe ────────────────
 *
 * O efeito dispara sempre que a conversa aberta tem não lidas e não há
 * marcação dela em andamento. A conversa que recebe dez mensagens enquanto
 * está aberta não vira dez POSTs ao mesmo tempo: enquanto um está no ar, os
 * outros esperam, e a resposta dele já traz o número de depois.
 *
 * E marca de novo quando chega mensagem com a conversa aberta. A versão
 * anterior guardava "já marquei esta" até a pessoa sair, e a mensagem nova
 * ficava contada como não lida diante de quem estava lendo (#841).
 *
 * O laço termina porque o servidor grava na projeção o número que o motor
 * relê: depois da marcação ele cai a zero, e zero não dispara. Falhar também
 * não repete: o efeito só roda de novo quando o número muda.
 *
 * O número que muda DURANTE a marcação não se perde: ao terminar, se ele já
 * não é o que foi marcado, o efeito confere de novo. Sem isso, uma marcação
 * que falhasse com mensagem nova no meio deixava a nova como não lida diante
 * de quem lia.
 *
 * ── Só com a aba à vista ─────────────────────────────────────────────────
 *
 * Conversa aberta numa aba escondida não está sendo lida. A mensagem que chega
 * ali continua não lida até a pessoa voltar para a aba, que é a mesma régua da
 * pilha de notificações para dispensar o card da conversa aberta.
 *
 * ── Por que só quando há o que marcar ────────────────────────────────────
 *
 * Marcar uma conversa que já está zerada é uma escrita na engine que não muda
 * nada — e o próprio Chatwoot reconhece isso, limitando esse caso a uma vez
 * por hora. Não gastamos a chamada.
 *
 * ── Falhar aqui é silencioso, de propósito ───────────────────────────────
 *
 * Ninguém pediu para marcar como lida: a pessoa pediu para abrir a conversa. Um
 * aviso vermelho por causa de um contador seria interromper a leitura para
 * falar de contabilidade. O número continua como estava, e a próxima abertura
 * tenta de novo.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ClienteDaApi } from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import { useAbaVisivel } from '@/shared/lib/aba-visivel';

export function useMarcarLida(
  api: ClienteDaApi | null,
  conversaId: string | null,
  naoLidas: number | undefined,
): void {
  const cache = useQueryClient();
  const emAndamento = useRef<Map<string, number>>(new Map());
  const naoLidasAgora = useRef(naoLidas);
  naoLidasAgora.current = naoLidas;
  const [conferencia, conferirDeNovo] = useState(0);
  const visivel = useAbaVisivel();

  const marcar = useMutation({
    mutationFn: (id: string) => {
      if (!api) throw new Error('sem sessão');
      return api.marcarConversaComoLida({ caminho: { id } });
    },
    onSuccess: (_resposta, id) => {
      /*
       * Tudo o que mostra o número, e o card.
       *
       * A Inbox e o cabeçalho da conversa leem a mesma coluna; o sino é outra
       * consulta, e sem ela o número dele só caía na próxima mensagem. As
       * notificações porque o servidor acabou de dar como vistos os cards
       * desta conversa.
       */
      void cache.invalidateQueries({ queryKey: chaves.inbox() });
      void cache.invalidateQueries({ queryKey: chaves.conversa(id) });
      void cache.invalidateQueries({ queryKey: chaves.naoLidas() });
      void cache.invalidateQueries({ queryKey: chaves.notificacoes() });
    },
    // Silêncio: ver o cabeçalho. O `onError` existe para o erro não subir como
    // rejeição não tratada.
    onError: () => undefined,
    onSettled: (_resposta, _erro, id) => {
      const marcado = emAndamento.current.get(id);
      emAndamento.current.delete(id);
      if (naoLidasAgora.current !== marcado) conferirDeNovo((n) => n + 1);
    },
  });

  const disparar = marcar.mutate;

  useEffect(() => {
    if (!api || !conversaId || !visivel) return;
    if (naoLidas === undefined || naoLidas <= 0) return;
    if (emAndamento.current.has(conversaId)) return;

    emAndamento.current.set(conversaId, naoLidas);
    disparar(conversaId);
  }, [api, conversaId, naoLidas, visivel, disparar, conferencia]);
}
